import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ClientTherapyPlanListItemDto } from '@client/shared/models/therapy-plan/client-therapy-plan-list-item.dto';
import { ClientTherapyPlanDto } from '@client/shared/models/therapy-plan/client-therapy-plan.dto';
import { ClientTherapyPlanApiService } from '@client/shared/services/api/client-therapy-plan-api.service';
import { ClientTherapyPlanProgressDto } from '@client/shared/models/therapy-plan/client-therapy-plan-progress.dto';
import { ClientExerciseTracingApiService } from '@client/shared/services/api/client-exercise-tracing-api.service';

@Injectable({ providedIn: 'root' })
export class ClientTherapyPlanStateService {

  readonly #therapyPlanApiService = inject(ClientTherapyPlanApiService);
  readonly #clientExerciseTracingApiService = inject(ClientExerciseTracingApiService);

  therapyPlans: WritableSignal<ClientTherapyPlanListItemDto[]> = signal([]);

  selectedTherapyPlanId: WritableSignal<string | null> = signal(null);
  selectedTherapyPlan: WritableSignal<ClientTherapyPlanDto | null> = signal(null);
  selectedTherapyPlanProgress: WritableSignal<ClientTherapyPlanProgressDto | null> = signal(null);
  selectedTherapyPlanTracingExercisesExplanationWatched: WritableSignal<Record<string, boolean>> = signal({});

  mainTherapyPlan: WritableSignal<ClientTherapyPlanDto | null> = signal(null);
  mainTherapyPlanProgress: WritableSignal<ClientTherapyPlanProgressDto | null> = signal(null);
  mainTherapyPlanTracing: WritableSignal<Record<string, boolean>> = signal({});

  isTherapyPlanLoaded: WritableSignal<boolean> = signal(false);

  isMainTherapyPlanLoaded = computed(() => {
    return this.mainTherapyPlan() !== null && this.mainTherapyPlanProgress() !== null;
  });

  loadAllTherapyPlans(): void {
    this.#therapyPlanApiService
      .getAll()
      .subscribe((data: ClientTherapyPlanListItemDto[]) => {
        this.setTherapyPlans(data);

        const mainPlan = data.find(t => t.isMainPlan)!;

        this.loadTherapyPlanById(mainPlan!.id, true);
        this.loadTherapyPlanProgress(mainPlan!.id, true);
      });
  }

  loadTherapyPlanById(id: string, setAsMain = false): void {
    this.isTherapyPlanLoaded.set(false);

    this.#therapyPlanApiService
      .getById(id)
      .subscribe((data: ClientTherapyPlanDto) => {
        this.setSelectedTherapyPlan(data);

        if (setAsMain) {
          this.mainTherapyPlan.set(data);
        }
      });
  }

  loadTherapyPlanProgress(therapyplanId: string, setAsMain = false): void {
    this.#therapyPlanApiService
      .getTherapyPlanProgress(therapyplanId)
      .subscribe((data: ClientTherapyPlanProgressDto) => {
        this.selectedTherapyPlanProgress.set(data);

        if (setAsMain) {
          this.mainTherapyPlanProgress.set(data);
        }
      });
  }

  loadExerciseTracingIsExplanationFirstWatched(therapyPlanId: string, setAsMain = false): void {
    this.#clientExerciseTracingApiService
      .getTherapyPlanExerciseTracingsIsExplanationWatched(therapyPlanId)
      .subscribe((data: Record<string, boolean>) => {
        this.selectedTherapyPlanTracingExercisesExplanationWatched.set(data);

        if (setAsMain) {
          this.mainTherapyPlanTracing.set(data);
        }
      });
  }

  setTherapyPlans(plans: ClientTherapyPlanListItemDto[]) {
    this.therapyPlans.set(plans ?? []);
  }

  setSelectedTherapyPlan(therapyPlan: ClientTherapyPlanDto | null) {
    this.selectedTherapyPlan.set(therapyPlan);
    therapyPlan ? this.selectedTherapyPlanId.set(therapyPlan.id) : this.selectedTherapyPlanId.set(null);
    this.isTherapyPlanLoaded.set(true);
  }

  setIsTherapyPlanLoaded(isLoaded: boolean) {
    this.isTherapyPlanLoaded.set(isLoaded);
  }

  setTherapyPlanProgress(progress: ClientTherapyPlanProgressDto | null) {
    this.selectedTherapyPlanProgress.set(progress);
    this.mainTherapyPlanProgress.set(progress);
  }
}
